import React, { useState, useMemo } from "react";
import {
    Box,
    Typography,
    Autocomplete,
    TextField,
    Alert,
    Grid,
} from "@mui/material";
import { UploadData } from "./UploadData";
import { config } from "../config";

// Хук для роботи з Excel-стовпцями
const useExcelColumns = () => {
    return useMemo(() => {
        const byIndex = {};   // 0 → 'A', 1 → 'B', ...
        const byName = {};    // 'A' → 0, 'B' → 1, ...

        let index = 0;

        // A–Z (0–25)
        for (let i = 65; i <= 90; i++) {
            const col = String.fromCharCode(i);
            byIndex[index] = col;
            byName[col] = index;
            index++;
        }

        // AA–ZZ (26–701)
        for (let i = 65; i <= 90; i++) {
            for (let j = 65; j <= 90; j++) {
                const col = String.fromCharCode(i) + String.fromCharCode(j);
                byIndex[index] = col;
                byName[col] = index;
                index++;
            }
        }

        return {
            byIndex,                  // індекс → літера
            byName,                   // літера → індекс
            list: Object.values(byIndex), // ['A', 'B', ..., 'ZZ'] — для Autocomplete
            total: index,             // 702
        };
    }, []);
};

export const UploadDataCourse = () => {
    const [groupColumnLetter, setGroupColumnLetter] = useState(null); // літера, яку бачить користувач
    const [courseColumnLetter, setCourseColumnLetter] = useState(null);

    const { list: excelColumnsList, byName } = useExcelColumns();

    // Конвертуємо вибрані букви в номери (0-based)
    const groupColumnIndex = groupColumnLetter ? byName[groupColumnLetter] : null;
    const courseColumnIndex = courseColumnLetter ? byName[courseColumnLetter] : null;

    // Формуємо URL з числовими параметрами
    const getUploadLink = () => {
        if (groupColumnIndex === null || courseColumnIndex === null) return null;

        const baseUrl = config.apiLinks.uploadGroupAndCoursesFromFile;
        const params = new URLSearchParams({
            groupColumn: groupColumnIndex,     // наприклад "3" (стовпець D)
            courseColumn: courseColumnIndex,   // наприклад "4" (стовпець E)
        });
        return `${baseUrl}?${params.toString()}`;
    };

    const uploadLink = getUploadLink();

    return (
        <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>
                Оновлення даних курсу з Excel-файлу
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Вкажіть стовпці Excel, де знаходяться назва групи та номер курсу.
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        options={excelColumnsList}
                        value={groupColumnLetter}
                        onChange={(event, newValue) => setGroupColumnLetter(newValue)}
                        noOptionsText="Стовпець не знайдено"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Стовпець з назвою групи"
                                required
                                placeholder="Наприклад: B або AB"
                                helperText="Почніть вводити літеру для пошуку"
                            />
                        )}
                        freeSolo={false}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Autocomplete
                        options={excelColumnsList}
                        value={courseColumnLetter}
                        onChange={(event, newValue) => setCourseColumnLetter(newValue)}
                        noOptionsText="Стовпець не знайдено"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Стовпець з номером курсу"
                                required
                                placeholder="Наприклад: C або AC"
                                helperText="Почніть вводити літеру для пошуку"
                            />
                        )}
                        freeSolo={false}
                        fullWidth
                    />
                </Grid>
            </Grid>

            {/* Фідбек: показуємо і букви, і номери для зручності */}
            {groupColumnLetter && courseColumnLetter && (
                <Alert severity="success" sx={{ mt: 4, mb: 3 }}>
                    Вибрано: назва групи — стовпець <strong>{groupColumnLetter}</strong> (№{groupColumnIndex}),{" "}
                    номер курсу — стовпець <strong>{courseColumnLetter}</strong> (№{courseColumnIndex})
                </Alert>
            )}

            {/* Компонент завантаження */}
            {uploadLink ? (
                <Box sx={{ mt: 4 }}>
                    <UploadData
                        title={`Завантажити файл (група: №${groupColumnIndex} [${groupColumnLetter}], курс: №${courseColumnIndex} [${courseColumnLetter}])`}
                        linkUpload={uploadLink}
                    />
                </Box>
            ) : (
                <Alert severity="info" sx={{ mt: 4 }}>
                    Оберіть обидва стовпці, щоб активувати завантаження файлу.
                </Alert>
            )}
        </Box>
    );
};